import { CommonModule, Location } from "@angular/common";
import {
    afterNextRender,
    Component,
    effect,
    inject,
    signal,
} from "@angular/core";
import { Router, RouterLink, RouterOutlet } from "@angular/router";
import { ReceiptService } from "@malirang/api-client";
import { ContainerComponent } from "apps/client/src/components/container/container.component";
import { injectNavigationEnd } from "ngxtension/navigation-end";
import { ReceiptFilterStore } from "./receipt-filter.store";
import { ReceiptStore } from "./receipt.store";
import { AlertService } from "../../../../../../libs/front-share/src";
import { CenterRoleStore } from "../../../stores/center/role.store";
import { environment } from "../../../env/env";

type Segment = "today" | "list" | "other-list" | "abnormal-payment" | "total";

/**
 * 수납 관리 페이지 레이아웃
 */
@Component({
    selector: "app-receipt",
    templateUrl: "./receipt.layout.html",
    styleUrls: ["./receipt.layout.scss"],
    imports: [CommonModule, ContainerComponent, RouterLink, RouterOutlet],
    providers: [ReceiptFilterStore],
})
export class ReceiptLayout {
    readonly receiptService = inject(ReceiptService);
    readonly router = inject(Router);
    readonly navigationEnd$ = injectNavigationEnd();
    readonly receiptStore = inject(ReceiptStore);
    readonly alertService = inject(AlertService);
    readonly location = inject(Location);
    readonly centerRoleStore = inject(CenterRoleStore);

    readRole = this.receiptStore.readRole;
    manageSelf = this.receiptStore.manageSelf;
    isFreePlan = this.receiptStore.isFreePlan;

    isProduction = false;

    segment = signal<Segment>("today");

    constructor() {
        afterNextRender(() => {
            this.setSegment();
            this.navigationEnd$.subscribe({
                next: () => {
                    this.setSegment();
                    this.handleGuard();
                },
            });

            this.handleGuard();
        });
    }

    handleGuard() {
        if (this.centerRoleStore.myRole()) {
            if (this.isFreePlan()) {
                this.alertService
                    .open({
                        title: "플랜 구독 후 이용 가능합니다.",
                        type: "info",
                        content: `해당 메뉴는 플랜 구독 후 이용 가능합니다.
                        구독을 원하시면 구독 페이지로 이동해주세요`,
                        buttons: {
                            cancel: {
                                text: "닫기",
                            },
                            confirm: {
                                text: "플랜 조회",
                            },
                        },
                    })
                    .closed.subscribe({
                        next: (res) => {
                            if (res?.action === "confirm") {
                                this.router.navigate(["/subscription"]);
                            } else {
                                this.location.back();
                            }
                        },
                    });
                return;
            }

            if (!this.readRole() && !this.manageSelf()) {
                this.alertService
                    .open({
                        title: "권한 없음",
                        type: "warning",
                        content: "수납 관리 권한이 없습니다.",
                        buttons: {
                            cancel: {
                                hidden: true,
                            },
                            confirm: {
                                color: "yellow",
                            },
                        },
                    })
                    .closed.subscribe({
                        next: () => this.location.back(),
                    });
            }
        }
    }

    setSegment() {
        const url = this.router.url;

        if (url.includes("/receipt/list")) {
            this.segment.set("list");
            return;
        } else if (url.includes("/receipt/total")) {
            this.segment.set("total");
            return;
        } else if (url.includes("/receipt/today")) {
            this.segment.set("today");
            return;
        } else if (url.includes("/receipt/other-list")) {
            this.segment.set("other-list");
            return;
        } else if (url.includes("/receipt/abnormal-payment")) {
            this.segment.set("abnormal-payment");
            return;
        }

        this.segment.set("today");
    }
}
